import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { X, DollarSign, Heart, Send } from 'lucide-react';
import ProfilePhoto from './ProfilePhoto';

const TipModal = ({ isOpen, onClose, onSendTip, onLikeOnly, userProfile }) => {
  const [tipAmount, setTipAmount] = useState('');
  const [tipMessage, setTipMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const predefinedAmounts = [5, 10, 25, 50, 100, 250, 500, 1000];

  const handleAmountSelect = (amount) => {
    setTipAmount(amount.toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tipAmount || parseFloat(tipAmount) < 1 || parseFloat(tipAmount) > 1000) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSendTip({
        amount: parseFloat(tipAmount),
        message: tipMessage.trim() || null
      });
      // Reset form
      setTipAmount('');
      setTipMessage('');
      onClose();
    } catch (error) {
      console.error('Error sending tip:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and one decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setTipAmount(value);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <DollarSign className="w-6 h-6 text-green-500 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">Send a Tip</h2>
          </div>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="rounded-full"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* User Info */}
        <div className="px-6 py-4 bg-gradient-to-r from-pink-50 to-red-50">
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full overflow-hidden mr-3 shrink-0">
              <ProfilePhoto src={userProfile.images?.[0]} alt={userProfile.name} compact testId="tip-modal-avatar" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{userProfile.name}</h3>
              <p className="text-sm text-gray-600">
                Increase your chance of matching by offering a tip!
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="p-6 overflow-y-auto min-h-0 flex-1">
          {/* Tip Amount */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Tip Amount ($1 - $1000)
            </label>
            
            {/* Quick Amount Buttons */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {predefinedAmounts.map((amount) => (
                <Button
                  key={amount}
                  type="button"
                  variant={tipAmount === amount.toString() ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleAmountSelect(amount)}
                  className={`text-sm ${
                    tipAmount === amount.toString()
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                      : 'hover:bg-green-50'
                  }`}
                >
                  ${amount}
                </Button>
              ))}
            </div>

            {/* Custom Amount Input */}
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                value={tipAmount}
                onChange={handleAmountChange}
                placeholder="Enter custom amount"
                className="pl-10 text-lg font-semibold"
              />
            </div>
            
            {tipAmount && (parseFloat(tipAmount) < 1 || parseFloat(tipAmount) > 1000) && (
              <p className="text-red-500 text-xs mt-1">
                Amount must be between $1 and $1000
              </p>
            )}
          </div>

          {/* Optional Message */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Optional Message
            </label>
            <textarea
              value={tipMessage}
              onChange={(e) => setTipMessage(e.target.value)}
              placeholder="Add a personal message (optional)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
              rows={3}
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">
              {tipMessage.length}/200 characters
            </p>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <Heart className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">How it works:</p>
                <ul className="space-y-1 text-xs">
                  <li>• Your tip offer will be shown to {userProfile.name}</li>
                  <li>• You'll exchange tip details privately if you both match</li>
                  <li>• FlameLink doesn't handle the actual payment</li>
                  <li>• Tip offers expire after 7 days</li>
                </ul>
              </div>
            </div>
          </div>
          </div>

          {/* Actions (sticky footer) */}
          <div className="p-4 sm:p-6 border-t border-gray-100 bg-white">
          {onLikeOnly && (
            <Button type="button" variant="secondary" onClick={onLikeOnly} disabled={isSubmitting} className="w-full mb-3" data-testid="like-without-tip-btn">
              💖 Like without a tip
            </Button>
          )}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              data-testid="tip-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="tip-send-btn"
              disabled={
                !tipAmount || 
                parseFloat(tipAmount) < 1 || 
                parseFloat(tipAmount) > 1000 || 
                isSubmitting
              }
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Tip & Like
                </>
              )}
            </Button>
          </div>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default TipModal;